import { AppLink } from "@/components/AppLink";
import { BlockageStatusBadge } from "@/components/badges/BlockageStatusBadge/BlockageStatusBadge";
import { Clock, CloudRain, Repeat } from "lucide-react";
import { cn } from "@/lib/utils";
import { format as d3format } from "d3-format";
import { formatDistanceToNowStrict } from "date-fns";

const fmtInt = d3format(".0f");

const formatRelativeTime = (dateStr: string | null): string | null => {
	if (!dateStr) return null;
	try {
		return formatDistanceToNowStrict(new Date(dateStr), { addSuffix: true });
	} catch {
		return null;
	}
};

const formatNextBlockage = (dateStr: string | null): { label: string; overdue: boolean } | null => {
	if (!dateStr) return null;
	try {
		const date = new Date(dateStr);
		const isOverdue = date.getTime() < Date.now();
		if (isOverdue) return { label: "Overdue", overdue: true };
		return { label: formatDistanceToNowStrict(date, { addSuffix: true }), overdue: false };
	} catch {
		return null;
	}
};

const BlockageDetailsGrid = ({ blockage }: { blockage: Dryp.Blockage }) => {
	const lastBlockage = formatRelativeTime(blockage.lastBlockageTime);
	const nextBlockage = formatNextBlockage(blockage.estimatedNextBlockageTime);
	const rainMm = blockage.accumulatedRain != null ? fmtInt(blockage.accumulatedRain * 1000) : null;
	const eventCount = blockage.events?.length ?? 0;

	const hasStats = lastBlockage || nextBlockage || rainMm != null || eventCount > 0;
	if (!hasStats) return null;

	return (
		<div className="grid grid-cols-2 gap-1.5">
			{lastBlockage && (
				<div className="bg-muted/50 rounded-md px-2 py-1.5">
					<p className="text-[10px] text-muted-foreground leading-none flex items-center gap-1">
						<Clock className="h-2.5 w-2.5" />
						Last blockage
					</p>
					<p className="text-xs font-semibold text-foreground mt-1 leading-none">{lastBlockage}</p>
				</div>
			)}
			{nextBlockage && (
				<div className="bg-muted/50 rounded-md px-2 py-1.5">
					<p className="text-[10px] text-muted-foreground leading-none flex items-center gap-1">
						<Clock className="h-2.5 w-2.5" />
						Est. next
					</p>
					<p
						className={cn(
							"text-xs font-semibold mt-1 leading-none",
							nextBlockage.overdue ? "text-red-600" : "text-foreground",
						)}
					>
						{nextBlockage.label}
					</p>
				</div>
			)}
			{eventCount > 0 && (
				<div className="bg-muted/50 rounded-md px-2 py-1.5">
					<p className="text-[10px] text-muted-foreground leading-none flex items-center gap-1">
						<Repeat className="h-2.5 w-2.5" />
						Events
					</p>
					<p className="text-xs font-semibold text-foreground mt-1 leading-none">{eventCount}</p>
				</div>
			)}
			{rainMm != null && (
				<div className="bg-muted/50 rounded-md px-2 py-1.5">
					<p className="text-[10px] text-muted-foreground leading-none flex items-center gap-1">
						<CloudRain className="h-2.5 w-2.5" />
						Rain (24h)
					</p>
					<p className="text-xs font-semibold text-foreground mt-1 leading-none">{rainMm} mm</p>
				</div>
			)}
		</div>
	);
};

interface BlockagePopUpProps {
	blockage: Dryp.Blockage;
	// Set false when composing this inside another popup that already shows
	// the structure title (e.g. on the general map, where StructurePopUp is
	// rendered above this).
	showTitle?: boolean;
}

export const BlockagePopUp: React.FC<BlockagePopUpProps> = ({ blockage, showTitle = true }) => {
	return (
		<div className={cn("px-2.5 py-2 space-y-2", !showTitle && "pt-1.5 border-t")}>
			{showTitle && (
				<AppLink
					to="Dashboard.Structures.Structure"
					params={{ id: blockage.structureId }}
					className="text-sm font-semibold text-foreground leading-tight hover:underline block"
				>
					{blockage.structureName}
				</AppLink>
			)}
			<BlockageStatusBadge status={blockage.status} />
			{blockage.status.statusDetail && (
				<p className="text-[11px] text-muted-foreground leading-relaxed">{blockage.status.statusDetail}</p>
			)}
			<BlockageDetailsGrid blockage={blockage} />
		</div>
	);
};
